import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, Pause, Play } from 'lucide-react';
import TrackList from '../components/PageComponents/trackList/TrackList';
import { HomeHeader } from '../components/PageComponents/header/HomeHeader';
import { AlbumCard } from '../components/PageComponents/cards/AlbumCard';
import { ArtistCard } from '../components/PageComponents/cards/ArtistCard';
import { Album } from '../components/PageComponents/albumList/album';
import { Artist } from '../components/PageComponents/artistList/artist';
import {
    getRecentAlbums,
    getTracksForAlbum,
    isTrackInAlbum,
} from '../components/PageComponents/albumList/getAllAlbums';
import { getRecentArtists } from '../components/PageComponents/artistList/getAllArtists';
import { coverArtUrl } from '../config/images';
import { AlbumArt } from '../components/PageComponents/cards/AlbumArt';
import { PlayerContext } from '../context/PlayerContext';
import '../components/PageComponents/cards/Cards.css';
import '../pages/Detail.css';
import './Home.css';

function formatTimeLeft(seconds: number): string {
    const clamped = Math.max(0, Math.round(seconds));
    const mins = Math.floor(clamped / 60);
    const secs = clamped % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} left`;
}

export default function Home(): React.JSX.Element {
    const navigate = useNavigate();
    const { tracks, refreshTracks, setQueue, currentSong, isPlaying, togglePlay, currentTime, duration } =
        useContext(PlayerContext);
    const [query, setQuery] = useState('');
    const [recentAlbums, setRecentAlbums] = useState<Album[]>([]);
    const [recentArtists, setRecentArtists] = useState<Artist[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        refreshTracks();
        (async () => {
            setLoading(true);
            const [albums, artists] = await Promise.all([getRecentAlbums(10), getRecentArtists(8)]);
            setRecentAlbums(albums);
            setRecentArtists(artists);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // "Continue listening" only has one real item to show today - whatever
    // track is currently loaded - since the backend keeps a single
    // now-playing position per user, not a history of separate resume
    // points across albums/playlists. Only shown once there's actual
    // progress on it, not for a freshly-queued track still at 0:00.
    const resumeDuration = duration > 0 ? duration : currentSong?.getDuration() ?? 0;
    const canResume = Boolean(currentSong?.getId()) && currentTime > 0 && resumeDuration > 0;
    const resumePct = canResume ? Math.min(100, (currentTime / resumeDuration) * 100) : 0;
    const resumeArt = coverArtUrl(currentSong?.getAlbumId());

    const showDashboard = query.trim() === '';

    return (
        <>
            <div className="pageHeader">
                <HomeHeader trackCount={tracks.length} query={query} onQueryChange={setQuery} />
            </div>
            <div className="pageContent">
                {showDashboard ? (
                    <div className="homeDashboard">
                        {canResume && (
                            <section className="detailRelated">
                                <h2 className="detailRelatedTitle">Continue listening</h2>
                                <div className="homeResumeCard" onClick={() => togglePlay()}>
                                    <div className="homeResumeArt">
                                        {resumeArt ? (
                                            <AlbumArt img={resumeArt} title={currentSong.getTitle()} iconSize={22} />
                                        ) : (
                                            <Music size={22} strokeWidth={2.75} />
                                        )}
                                    </div>
                                    <div className="homeResumeMeta">
                                        <span className="homeResumeTitle">{currentSong.getTitle()}</span>
                                        <span className="homeResumeSubtitle">
                                            {currentSong.getArtist()} · {currentSong.getAlbum()}
                                        </span>
                                        <div className="homeResumeProgress">
                                            <div className="homeResumeProgressFill" style={{ width: `${resumePct}%` }} />
                                        </div>
                                        <span className="homeResumeMuted">
                                            {formatTimeLeft(resumeDuration - currentTime)}
                                        </span>
                                    </div>
                                    <button
                                        className="homeResumePlayButton"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            togglePlay();
                                        }}
                                        aria-label={isPlaying ? 'Pause' : 'Resume'}
                                    >
                                        {isPlaying ? (
                                            <Pause size={16} strokeWidth={2.75} fill="currentColor" />
                                        ) : (
                                            <Play size={16} strokeWidth={2.75} fill="currentColor" />
                                        )}
                                    </button>
                                </div>
                            </section>
                        )}

                        <section className="detailRelated">
                            <div className="detailRelatedHeader">
                                <h2 className="detailRelatedTitle">Recently added</h2>
                                <span className="homeSectionLink" onClick={() => navigate('/albums')}>
                                    Albums →
                                </span>
                            </div>
                            {loading ? (
                                <div className="trackListEmpty">Loading…</div>
                            ) : recentAlbums.length === 0 ? (
                                <div className="trackListEmpty">No albums in library</div>
                            ) : (
                                <div className="cardGrid">
                                    {recentAlbums.map((album) => {
                                        const isThisAlbumCurrent = isTrackInAlbum(currentSong, album);
                                        return (
                                            <AlbumCard
                                                key={album.getId()}
                                                album={album}
                                                onClick={(a) => navigate(`/albums/${a.getId()}`)}
                                                isPlaying={isThisAlbumCurrent && isPlaying}
                                                onPlay={(a) => {
                                                    if (isThisAlbumCurrent) togglePlay();
                                                    else setQueue(getTracksForAlbum(tracks, a), 0);
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        <section className="detailRelated">
                            <div className="detailRelatedHeader">
                                <h2 className="detailRelatedTitle">Artists</h2>
                                <span className="homeSectionLink" onClick={() => navigate('/artists')}>
                                    Artists →
                                </span>
                            </div>
                            {loading ? (
                                <div className="trackListEmpty">Loading…</div>
                            ) : recentArtists.length === 0 ? (
                                <div className="trackListEmpty">No artists in library</div>
                            ) : (
                                <div className="cardGrid homeArtistGrid">
                                    {recentArtists.map((artist) => {
                                        const isThisArtistCurrent = currentSong?.getArtists().some((a) => a.id === artist.getId());
                                        return (
                                            <ArtistCard
                                                key={artist.getId()}
                                                artist={artist}
                                                onClick={(a) => navigate(`/artists/${a.getId()}`)}
                                                isPlaying={isThisArtistCurrent && isPlaying}
                                                onPlay={(a) => {
                                                    if (isThisArtistCurrent) togglePlay();
                                                    else setQueue(tracks.filter((t) => t.getArtists().some((ca) => ca.id === a.getId())), 0);
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    </div>
                ) : (
                    <TrackList query={query} />
                )}
            </div>
        </>
    );
}
